/**
 * The live AI call dock's state (ring R5): the calls the stream reported, and
 * which of them the dock shows right now.
 *
 * The server is the truth and this file only keeps it tidy:
 *  - a `snapshot` replaces everything (the stream opened, or reopened after a
 *    drop, and whatever happened meanwhile is in it);
 *  - a `call` replaces the facts, and brings lines only when the server caught
 *    them up; a call the operator may not hear, or whose callee refused the
 *    recording, keeps no lines at all;
 *  - a `turn` is merged by its number and placed by when it was said, so a
 *    line that arrives twice is one line and a Hallo that came back as text
 *    after the opening started still reads before it;
 *  - a stopped recording of a taken-over call stays stopped (ring R6c).
 *
 * WHAT THE DOCK SHOWS: every call still going (one still dialling or ringing
 * for `DOCK_RING_STALE_MS` at most, the hub's own rule), and an ended call for
 * `DOCK_ENDED_SHOW_MS` after it ended (time from the server's clock: a browser
 * clock that is minutes off must not hide a call or keep an old one), unless
 * the operator closed it. Going calls first, newest first.
 *
 * Pure: no React, no clock of its own. Every time comes in as an argument.
 */
import type { LiveCall, LiveGoneEvent, LiveSnapshot, LiveTurn, LiveTurnEvent } from "../../../lib/aiLive";

/** How long an ended call stays on the dock. */
export const DOCK_ENDED_SHOW_MS = 20_000;
/** How long an ended call is kept at all, for a result that arrives after it left the dock. */
export const DOCK_ENDED_KEEP_MS = 2 * 60_000;
/** A call not ended by then is not going on: the server drops it too. */
export const DOCK_STALE_MS = 20 * 60_000;
/** A call still dialling or ringing by then never rang through: its last status post was lost. */
export const DOCK_RING_STALE_MS = 3 * 60_000;

/** How long a call not ended yet may be in this phase before the dock stops believing it. */
function staleAfter(phase: LiveCall["phase"]): number {
  return phase === "dialing" || phase === "ringing" ? DOCK_RING_STALE_MS : DOCK_STALE_MS;
}

export interface AiLiveState {
  calls: Record<string, LiveCall>;
  /** The server's clock minus the browser's, measured at the last snapshot. */
  skewMs: number;
  /** Calls the operator closed on the dock. */
  dismissed: string[];
  /** The stream delivered a snapshot and has not dropped since. */
  live: boolean;
}

export const INITIAL_AI_LIVE: AiLiveState = { calls: {}, skewMs: 0, dismissed: [], live: false };

export type AiLiveAction =
  | { type: "snapshot"; snapshot: LiveSnapshot; receivedAt: number }
  | { type: "call"; call: LiveCall }
  | { type: "turn"; event: LiveTurnEvent }
  /** A colleague took the call over: it is not this operator's to watch any more. */
  | { type: "gone"; event: LiveGoneEvent }
  /** The server confirmed this operator's stop of a taken-over call's recording. */
  | { type: "recordingStopped"; id: string }
  | { type: "dismiss"; id: string }
  | { type: "down" }
  | { type: "prune"; now: number };

/** Lines by when they were said; equal times by arrival. */
function ordered(turns: LiveTurn[]): LiveTurn[] {
  return [...turns].sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.seq - b.seq);
}

/** `extra` into `base`, one line per number. */
export function mergeTurns(base: readonly LiveTurn[], extra: readonly LiveTurn[]): LiveTurn[] {
  if (extra.length === 0) return [...base];
  const bySeq = new Map(base.map((turn) => [turn.seq, turn]));
  for (const turn of extra) bySeq.set(turn.seq, turn);
  return ordered([...bySeq.values()]);
}

/** Whether this call may carry lines on this dock: the callee's refusal is the only no. */
function keepsWords(call: Pick<LiveCall, "recordObjected">): boolean {
  return !call.recordObjected;
}

/**
 * A stopped recording stays stopped, the hub's own rule (ring R6c): the stop
 * column is never cleared, so a call or a snapshot still saying "recording" left
 * the server before the stop, and must not light the dot or bring the button back.
 */
function keepStopped(prev: LiveCall | undefined, next: LiveCall): LiveCall {
  return prev?.personRecording === "stopped" ? { ...next, personRecording: "stopped", canStopRecording: false } : next;
}

/** Milliseconds on the server's clock for a browser time. */
export function serverTime(state: Pick<AiLiveState, "skewMs">, browserNow: number): number {
  return browserNow + state.skewMs;
}

/** A server ISO time on the browser's clock, for a timer that counts in the browser. */
export function browserTime(state: Pick<AiLiveState, "skewMs">, iso: string | null): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  return Number.isFinite(at) ? at - state.skewMs : null;
}

function prune(calls: Record<string, LiveCall>, serverNow: number): Record<string, LiveCall> {
  const kept: Record<string, LiveCall> = {};
  for (const call of Object.values(calls)) {
    const ended = call.phase === "ended" && call.endedAt ? Date.parse(call.endedAt) : Number.NaN;
    if (Number.isFinite(ended)) {
      if (serverNow - ended > DOCK_ENDED_KEEP_MS) continue;
    } else if (serverNow - Date.parse(call.startedAt) > staleAfter(call.phase)) {
      // Also an ended call with no end time: never shown, and not kept forever either.
      continue;
    }
    kept[call.id] = call;
  }
  return kept;
}

export function aiLiveReducer(state: AiLiveState, action: AiLiveAction): AiLiveState {
  switch (action.type) {
    case "snapshot": {
      const skewMs = Date.parse(action.snapshot.serverNow) - action.receivedAt;
      const calls: Record<string, LiveCall> = {};
      for (const call of action.snapshot.calls) {
        calls[call.id] = keepStopped(state.calls[call.id], { ...call, turns: keepsWords(call) ? ordered(call.turns) : [] });
      }
      return {
        calls,
        skewMs: Number.isFinite(skewMs) ? skewMs : 0,
        dismissed: state.dismissed.filter((id) => id in calls),
        live: true,
      };
    }
    case "call": {
      const prev = state.calls[action.call.id];
      const turns = keepsWords(action.call) ? mergeTurns(prev?.turns ?? [], action.call.turns) : [];
      return { ...state, calls: { ...state.calls, [action.call.id]: keepStopped(prev, { ...action.call, turns }) } };
    }
    case "turn": {
      const call = state.calls[action.event.callLogId];
      if (!call || !keepsWords(call)) return state;
      return {
        ...state,
        calls: { ...state.calls, [call.id]: { ...call, turns: mergeTurns(call.turns, [action.event.turn]) } },
      };
    }
    case "gone": {
      // A colleague took the call over, so the server will send nothing more
      // about it. The card is ended here rather than removed, so it fades on
      // the usual clock with a line saying what happened instead of vanishing
      // mid-sentence.
      const call = state.calls[action.event.callLogId];
      if (!call) return state;
      return {
        ...state,
        calls: {
          ...state.calls,
          [call.id]: {
            ...call,
            phase: "ended",
            endedAt: call.endedAt ?? action.event.at,
            takeover: call.takeover
              ? { ...call.takeover, stage: "bridged", mine: false, failure: null }
              : { stage: "bridged", via: "", failure: null, mine: false },
            canTakeOver: false,
            canStopRecording: false,
          },
        },
      };
    }
    case "recordingStopped": {
      // Only after the server said yes, never on the press: the operator is
      // about to tell somebody the recording has stopped. Put here rather than
      // left to the stream so the dot goes out with the answer, not a read later.
      const call = state.calls[action.id];
      if (!call || call.personRecording === "stopped") return state;
      return {
        ...state,
        calls: { ...state.calls, [call.id]: { ...call, personRecording: "stopped", canStopRecording: false } },
      };
    }
    case "dismiss":
      return state.dismissed.includes(action.id) ? state : { ...state, dismissed: [...state.dismissed, action.id] };
    case "down":
      return state.live ? { ...state, live: false } : state;
    case "prune": {
      const calls = prune(state.calls, serverTime(state, action.now));
      if (Object.keys(calls).length === Object.keys(state.calls).length) return state;
      return { ...state, calls, dismissed: state.dismissed.filter((id) => id in calls) };
    }
  }
}

/** The calls the dock shows at `browserNow`: going ones first, then the recently ended, newest first. */
export function dockCalls(state: AiLiveState, browserNow: number): LiveCall[] {
  const now = serverTime(state, browserNow);
  const shown = Object.values(state.calls).filter((call) => {
    if (state.dismissed.includes(call.id)) return false;
    if (call.phase !== "ended") return now - Date.parse(call.startedAt) <= staleAfter(call.phase);
    const ended = call.endedAt ? Date.parse(call.endedAt) : Number.NaN;
    return Number.isFinite(ended) && now - ended <= DOCK_ENDED_SHOW_MS;
  });
  return shown.sort((a, b) => {
    const going = Number(a.phase === "ended") - Number(b.phase === "ended");
    return going || Date.parse(b.startedAt) - Date.parse(a.startedAt);
  });
}

/** How an ended call ended, as catalogue suffixes under `aiCalls.live.end`. */
export const END_REASONS = [
  // The takeover words come first, because they describe what happened to the
  // conversation rather than what Twilio's last status post said about the line.
  "takenOver",
  "takenOverMine",
  "takeoverFailed",
  "recordObjected",
  "stopped",
  "machine",
  "notDialled",
  "noAnswer",
  "busy",
  "failed",
  "canceled",
  "completed",
] as const;
export type EndReason = (typeof END_REASONS)[number];

export function endReason(
  call: Pick<LiveCall, "recordObjected" | "stopped" | "answeredBy" | "outcome" | "status" | "takeover">,
): EndReason {
  const takeover = call.takeover;
  if (takeover && !takeover.failure && (takeover.stage === "handed" || takeover.stage === "bridged")) {
    return takeover.mine ? "takenOverMine" : "takenOver";
  }
  // A takeover that was tried and lost is worth saying: the card is the only
  // place an operator learns their own attempt did not work.
  if (takeover?.failure && takeover.mine) return "takeoverFailed";
  if (call.recordObjected) return "recordObjected";
  if (call.stopped) return "stopped";
  const answeredBy = (call.answeredBy ?? "").toLowerCase();
  if (answeredBy.startsWith("machine_") || answeredBy === "fax") return "machine";
  if (call.outcome === "not_dialled") return "notDialled";
  switch (call.status) {
    case "no-answer":
      return "noAnswer";
    case "busy":
      return "busy";
    case "failed":
      return "failed";
    case "canceled":
      return "canceled";
    default:
      return "completed";
  }
}

/**
 * How many characters of the number are on screen while it is dialled: one
 * more every `stepMs`, all of it once the call rings, or at once when motion
 * is reduced.
 */
export function dialledChars(number: string, sinceMs: number, stepMs: number, reduced: boolean): number {
  if (reduced || !Number.isFinite(sinceMs) || stepMs <= 0) return number.length;
  return Math.min(number.length, Math.floor(Math.max(0, sinceMs) / stepMs) + 1);
}
